import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BaseLayout from './BaseLayout';
import FlameLoader from './FlameLoader';
import axios from 'axios';
import { API_BASE } from '../api';
import { useAuth } from "../context/AuthContext";
import { isGameLocked } from '../teamUtils';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import 'bootstrap/dist/css/bootstrap.min.css';

// Explicit mapping from full database name (lowercase) to short display name
const TEAM_NAME_MAP = {
  // NBA
  'philadelphia 76ers':        '76ers',
  'portland trail blazers':    'Blazers',
  'milwaukee bucks':           'Bucks',
  'chicago bulls':             'Bulls',
  'cleveland cavaliers':       'Cavaliers',
  'boston celtics':            'Celtics',
  'los angeles clippers':      'Clippers',
  'memphis grizzlies':         'Grizzlies',
  'atlanta hawks':             'Hawks',
  'miami heat':                'Heat',
  'charlotte hornets':         'Hornets',
  'sacramento kings':          'Kings',
  'new york knicks':           'Knicks',
  'los angeles lakers':        'Lakers',
  'dallas mavericks':          'Mavericks',
  'brooklyn nets':             'Nets',
  'denver nuggets':            'Nuggets',
  'indiana pacers':            'Pacers',
  'new orleans pelicans':      'Pelicans',
  'detroit pistons':           'Pistons',
  'toronto raptors':           'Raptors',
  'houston rockets':           'Rockets',
  'san antonio spurs':         'Spurs',
  'phoenix suns':              'Suns',
  'oklahoma city thunder':     'Thunder',
  'minnesota timberwolves':    'Timberwolves',
  'golden state warriors':     'Warriors',
  'washington wizards':        'Wizards',
  // EPL
  'arsenal':                   'Arsenal',
  'aston villa':                'Aston Villa',
  'bournemouth':               'Bournemouth',
  'brentford':                 'Brentford',
  'brighton and hove albion':  'Brighton',
  'burnley':                   'Burnley',
  'chelsea':                   'Chelsea',
  'crystal palace':            'Crystal Palace',
  'everton':                   'Everton',
  'fulham':                    'Fulham',
  'leeds united':              'Leeds',
  'liverpool':                 'Liverpool',
  'manchester city':           'Manchester City',
  'manchester united':         'Manchester United',
  'newcastle united':          'Newcastle',
  'nottingham forest':         'Nottingham Forest',
  'tottenham hotspur':         'Tottenham',
  'west ham united':           'West Ham',
  'wolverhampton wanderers':   'Wolves',
  'sunderland':                'Sunderland',
  // MLS
  'atlanta united':            'Atlanta',
  'austin fc':                 'Austin',
  'charlotte fc':              'Charlotte',
  'chicago fire':              'Chicago',
  'fc cincinnati':             'Cincinnati',
  'colorado rapids':           'Colorado',
  'columbus crew':             'Columbus',
  'fc dallas':                 'Dallas',
  'dc united':                 'DC United',
  'houston dynamo':            'Houston',
  'sporting kansas city':      'Kansas City',
  'la galaxy':               'L.A. Galaxy',
  'los angeles fc':            'Los Angeles',
  'inter miami':               'Miami',
  'minnesota united':          'Minnesota',
  'cf montréal':               'Montreal',
  'nashville sc':              'Nashville',
  'new england revolution':    'New England',
  'new york city fc':          'New York City',
  'new york red bulls':        'New York',
  'orlando city':              'Orlando',
  'philadelphia union':        'Philadelphia',
  'portland timbers':          'Portland',
  'real salt lake':            'Salt Lake',
  'san diego fc':              'San Diego',
  'san jose earthquakes':      'San Jose',
  'seattle sounders':          'Seattle',
  'st. louis city sc':         'St. Louis',
  'toronto fc':                'Toronto',
  'vancouver whitecaps':       'Vancouver',
  // MLB
  'los angeles angels':        'Angels',
  'houston astros':            'Astros',
  'athletics':                 'Athletics',
  'toronto blue jays':         'Blue Jays',
  'atlanta braves':            'Braves',
  'milwaukee brewers':         'Brewers',
  'st. louis cardinals':       'Cardinals',
  'chicago cubs':              'Cubs',
  'arizona diamondbacks':      'Diamondbacks',
  'los angeles dodgers':       'Dodgers',
  'san francisco giants':      'Giants',
  'cleveland guardians':       'Guardians',
  'seattle mariners':          'Mariners',
  'miami marlins':             'Marlins',
  'new york mets':             'Mets',
  'washington nationals':      'Nationals',
  'baltimore orioles':         'Orioles',
  'san diego padres':          'Padres',
  'philadelphia phillies':     'Phillies',
  'pittsburgh pirates':        'Pirates',
  'texas rangers':             'Rangers',
  'tampa bay rays':            'Rays',
  'boston red sox':            'Red Sox',
  'cincinnati reds':           'Reds',
  'colorado rockies':          'Rockies',
  'kansas city royals':        'Royals',
  'detroit tigers':            'Tigers',
  'minnesota twins':           'Twins',
  'chicago white sox':         'White Sox',
  'new york yankees':          'Yankees',
};

const getTeamDisplayName = (fullName) => {
  const lower = fullName.toLowerCase().trim();
  if (TEAM_NAME_MAP[lower]) return TEAM_NAME_MAP[lower];
  // Fallback: return the last word, capitalised
  const words = fullName.trim().split(/\s+/);
  const last = words[words.length - 1];
  return last.charAt(0).toUpperCase() + last.slice(1).toLowerCase();
};

// ── XP level table (mirrors achievements_engine.py XP_THRESHOLDS) ────────────
const XP_LEVELS = [
  [0, 1, 'Rookie'], [100, 2, 'Amateur'], [250, 3, 'Contender'],
  [500, 4, 'Analyst'], [1000, 5, 'Expert'], [2000, 6, 'Veteran'],
  [3500, 7, 'Pro'], [5000, 8, 'Elite'], [7500, 9, 'Master'], [10000, 10, 'Oracle'],
];

const getLevelInfo = (xp = 0) => {
  let cur = XP_LEVELS[0];
  for (const e of XP_LEVELS) { if (xp >= e[0]) cur = e; }
  const next = XP_LEVELS[cur[1]]; // 1-based level == 0-based index of next entry
  const nextThr = next ? next[0] : null;
  const pct = nextThr !== null
    ? Math.min(100, Math.round(((xp - cur[0]) / (nextThr - cur[0])) * 100))
    : 100;
  return { levelName: cur[2], pct, nextThr };
};

const getAvatarSrc = (pic) => {
  if (!pic) return '/default.jpg';
  if (pic.startsWith('http') || pic.startsWith('data:')) return pic;
  return `data:image/jpeg;base64,${pic}`;
};

const RecentUpdatesModal = ({ show, handleClose }) => {
  const newUpdates = [
    { title: 'Game Year Fix', description: 'Fixed issue with 2025 year change causing 2024 games to appear.' },
    { title: 'Image Hosting', description: 'Profile pictures are no longer stored via base64 text. They are now hosted via an image hosting service. Logins/profile loads are now 10x faster! 🔥🔥🔥 (I made that number up, but it\'s definitely faster!) 🔥🔥🔥' },
    { title: 'Clean URLs', description: 'Switched from HashRouter to BrowserRouter by implementing a 404 redirect on refreshes. No more /#/.' },
    { title: 'More Changes to Come!', description: 'Stay tuned.' },
  ];

  const updates = newUpdates;
  const modalTitle = '! Update !';

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{modalTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
          {updates.map((update, index) => (
            <li key={index} style={{ marginBottom: '10px' }}>
              <strong>{update.title}</strong>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#555' }}>{update.description}</p>
            </li>
          ))}
        </ul>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const UsernameWelcomeModal = ({ show, handleClose, currentUsername, token, onUsernameChanged }) => {
  const [newUsername, setNewUsername] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleChange = async () => {
    const usernameRegex = /^[a-zA-Z0-9_-]{3,25}$/;
    if (!newUsername.trim()) {
      setMessage('Please enter a username.');
      return;
    }
    if (!usernameRegex.test(newUsername.trim())) {
      setMessage('Username must be 3–25 characters: letters, numbers, underscores, and hyphens only.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}/user/update-username`,
        { username: newUsername.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUsernameChanged(res.data.username);
      handleClose();
    } catch (err) {
      setMessage(err.response?.data?.error || 'An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Usernames</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Since your last visit, we’ve added <strong>usernames</strong> to Flame Picks.
          You’ve been automatically assigned one based on your email address.
        </p>
        <p style={{ marginBottom: '0.4rem' }}>
          Your current username is: <strong>@{currentUsername}</strong>
        </p>
        <p style={{ fontSize: '0.9rem', color: '#888', marginBottom: '1rem' }}>
          You can keep it, or pick something new below.
          You can always update it later in <strong>Settings</strong>.
        </p>
        <input
          type="text"
          className="form-control"
          placeholder={`New username (leave blank to keep @${currentUsername})`}
          value={newUsername}
          onChange={e => { setNewUsername(e.target.value); setMessage(''); }}
          autoComplete="username"
        />
        {message && <p style={{ color: 'var(--red)', fontSize: '0.85rem', marginTop: '0.4rem', marginBottom: 0 }}>{message}</p>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Keep @{currentUsername}
        </Button>
        <Button variant="primary" onClick={handleChange} disabled={submitting || !newUsername.trim()}>
          {submitting ? 'Saving…' : 'Change Username'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const MatchPick = ({ homeTeam, awayTeam, gameid, onPickSelected, isEPL, isLoading, initialPick }) => {
  const [pick, setPick] = useState(initialPick || null);
  const navigate = useNavigate();

  // homeTeam/awayTeam are the full backend names (e.g. "DENVER NUGGETS").
  // homeDisplay/awayDisplay are the short UI labels (e.g. "Nuggets").
  const homeDisplay = getTeamDisplayName(homeTeam);
  const awayDisplay = getTeamDisplayName(awayTeam);

  const handlePick = (team) => {
    setPick(pick === team ? null : team);
    onPickSelected(pick === team ? null : team, gameid);
  };

  // Navigate to the team page by searching TheSportsDB for the team's ID.
  const handleLogoClick = async (e, fullTeamName) => {
    e.stopPropagation();
    try {
      const res = await axios.get(`${API_BASE}/sports/search-teams`, {
        params: { q: fullTeamName.toLowerCase() }
      });
      const team = res.data.teams?.[0];
      if (team?.idTeam) navigate(`/team/${team.idTeam}`);
    } catch {}
  };

  return (
    <div
      className="match-card"
      style={{ cursor: 'pointer' }}
      onClick={() => navigate(`/game/${gameid}`)}
    >
      <div className="mc-team mc-home">
        <img
          src={`/Logos/${homeDisplay.charAt(0).toUpperCase() + homeDisplay.slice(1).toLowerCase()}.png`}
          alt={homeDisplay}
          className="team-logo"
          style={{ cursor: 'pointer' }}
          onClick={(e) => handleLogoClick(e, homeTeam)}
        />
        <button
          onClick={(e) => { e.stopPropagation(); handlePick(homeTeam); }}
          className={`team-btn${pick === homeTeam ? ' selected' : ''}`}
        >
          {homeDisplay}
        </button>
      </div>
      <div className="vs-divider" onClick={(e) => e.stopPropagation()}>
        VS.
        {isEPL && !isLoading && (
          <button
            onClick={(e) => { e.stopPropagation(); handlePick("Draw"); }}
            className={`draw-btn${pick === "Draw" ? ' selected' : ''}`}
          >
            DRAW
          </button>
        )}
      </div>
      <div className="mc-team mc-away">
        <button
          onClick={(e) => { e.stopPropagation(); handlePick(awayTeam); }}
          className={`team-btn${pick === awayTeam ? ' selected' : ''}`}
        >
          {awayDisplay}
        </button>
        <img
          src={`/Logos/${awayDisplay.charAt(0).toUpperCase() + awayDisplay.slice(1).toLowerCase()}.png`}
          alt={awayDisplay}
          className="team-logo"
          style={{ cursor: 'pointer' }}
          onClick={(e) => handleLogoClick(e, awayTeam)}
        />
      </div>
    </div>
  );
};

const ExtinguishedFlame = () => (
  <div className="ef-container">
    <div className="ef-wrap">
      <div className="ef-smoke ef-smoke-1" />
      <div className="ef-smoke ef-smoke-2" />
      <div className="ef-smoke ef-smoke-3" />
      <div className="flame-part fp-outer" />
      <div className="flame-part fp-mid" />
      <div className="flame-part fp-inner" />
      <div className="flame-part fp-core" />
      <div className="flame-base" />
      <div className="ef-wick" />
    </div>
  </div>
);

const HomePage = ({ isAuthenticated, user }) => {
  const { authData, updateUserAttribute } = useAuth();
  const navigate = useNavigate();
  const [eventsByDate, setEventsByDate] = useState({});
  const [gameVisibility, setGameVisibility] = useState({});
  const [picks, setPicks] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('fp_picks_draft') || '{}'); } catch { return {}; }
  });
  const [loading, setLoading] = useState(true);
  const [submittingAll, setSubmittingAll] = useState(false);
  const [selectedSport, setSelectedSport] = useState(
    () => sessionStorage.getItem('fp_sport_draft') || 'NBA'
  );
  const [allPicksMade, setAllPicksMade] = useState({
    NBA: false, EPL: false, MLB: false, MLS: false
  });
  const [showModal, setShowModal] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [globalFeedback, setGlobalFeedback] = useState(null);
  const [pendingSportSwitch, setPendingSportSwitch] = useState(null);
  const [homeSummary, setHomeSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Fetch dashboard data once on mount and after pick submission
  const fetchHomeSummary = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) { setSummaryLoading(false); return; }
    try {
      const res = await axios.get(`${API_BASE}/user/home-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHomeSummary(res.data);
    } catch (err) {
      console.error('Home summary error:', err);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => { fetchHomeSummary(); }, [fetchHomeSummary]);

  useEffect(() => {
    document.body.classList.add("account-page-bg");
    const fetchAndGroupEventsByDate = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          console.error('No authentication token found');
          return;
        }

        const eventsPromise = axios.get(`${API_BASE}/data/get-upcoming-games`, { params: { sport: selectedSport } }).then(r => r.data.games);
        const picksPromise = axios.get(`${API_BASE}/user/get-picks`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        const [events, picksResponse] = await Promise.all([eventsPromise, picksPromise]);

        const userPicks = new Set(picksResponse.data.map(pick => pick.game_id));
        const currentDate = new Date();
        currentDate.setMinutes(currentDate.getMinutes());

        const filteredAndGroupedEvents = events
          .filter(event => !userPicks.has(event.game_id))
          .filter(event => !isGameLocked(event))
          .reduce((acc, event) => {
            const [day, month, year] = event.date.split('/').map(Number);
            const eventYear = year || currentDate.getFullYear();
            const timeParts = event.time.match(/(\d+):(\d+)(am|pm)/);
            let hour = Number(timeParts[1]);
            if (timeParts[3] === 'pm' && hour !== 12) hour += 12;
            if (timeParts[3] === 'am' && hour === 12) hour = 0;
            const gameDate = new Date(Date.UTC(eventYear, month - 1, day, hour));
            gameDate.setHours(gameDate.getHours() - 4);
            const adjustedDate = `${gameDate.getDate().toString().padStart(2, '0')}/${(gameDate.getMonth() + 1).toString().padStart(2, '0')}/${gameDate.getFullYear()}`;
            acc[adjustedDate] = acc[adjustedDate] || [];
            acc[adjustedDate].push({ ...event, adjustedDate });
            return acc;
          }, {});

        setEventsByDate(filteredAndGroupedEvents);

        const initialGameVisibility = Object.keys(filteredAndGroupedEvents).reduce((acc, date) => {
          acc[date] = filteredAndGroupedEvents[date].reduce((acc, game) => {
            acc[game.game_id] = true;
            return acc;
          }, {});
          return acc;
        }, {});

        setGameVisibility(initialGameVisibility);

        const validGameIds = new Set(
          Object.values(filteredAndGroupedEvents).flat().map(e => e.game_id)
        );
        setPicks(prev => {
          const cleaned = {};
          for (const [date, datePicks] of Object.entries(prev)) {
            const filtered = datePicks.filter(p => validGameIds.has(p.gameid));
            if (filtered.length > 0) cleaned[date] = filtered;
          }
          return cleaned;
        });

      } catch (error) {
        console.error('Error fetching events or user picks:', error);
      }
      setLoading(false);
    };

    fetchAndGroupEventsByDate();
    return () => { document.body.classList.remove("account-page-bg"); };
  }, [selectedSport]);

  useEffect(() => {
    if (authData.user?.last_login) {
      const lastLogin = new Date(authData.user.last_login);
      const specifiedDate = new Date('2023-06-06');
      if (lastLogin < specifiedDate) {
        setShowModal(true);
        updateUserAttribute("last_login", null);
      }
    }
  }, [authData, updateUserAttribute]);

  useEffect(() => {
    const userId = authData.user?.id;
    if (!userId) return;
    const seenKey = `fp_username_seen_${userId}`;
    if (localStorage.getItem(seenKey)) return;
    const rawLogin = authData.user?.last_login;
    if (rawLogin) {
      const lastLogin = new Date(rawLogin);
      if (lastLogin < new Date('2026-01-01')) {
        setShowUsernameModal(true);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData.user?.id]);

  useEffect(() => {
    sessionStorage.setItem('fp_picks_draft', JSON.stringify(picks));
  }, [picks]);

  useEffect(() => {
    sessionStorage.setItem('fp_sport_draft', selectedSport);
  }, [selectedSport]);

  const handleSubmitAllPicks = async () => {
    const postData = Object.entries(picks).flatMap(([, datePicks]) =>
      datePicks.map(p => ({ game_id: p.gameid, sport: selectedSport, picked_team: p.team }))
    );
    if (postData.length === 0) return;

    setSubmittingAll(true);
    const token = localStorage.getItem("token");
    if (!token) { setSubmittingAll(false); return; }

    try {
      await axios.post(`${API_BASE}/user/make-pick`, postData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Compute new visibility synchronously so allPicksMade is set
      // in the same render batch — avoids the stale-state race condition.
      const newVis = { ...gameVisibility };
      Object.entries(picks).forEach(([date, datePicks]) => {
        datePicks.forEach(p => {
          if (newVis[date]) newVis[date][p.gameid] = false;
        });
      });
      setGameVisibility(newVis);
      setPicks({});
      sessionStorage.removeItem('fp_picks_draft');
      const allDays = Object.keys(eventsByDate);
      const allPicked = allDays.every(day =>
        eventsByDate[day].every(game => !newVis[day]?.[game.game_id])
      );
      setAllPicksMade(prev => ({ ...prev, [selectedSport]: allPicked }));
      const count = postData.length;
      setGlobalFeedback({ type: 'success', msg: `${count} pick${count !== 1 ? 's' : ''} submitted!` });
      setTimeout(() => setGlobalFeedback(null), 3000);
      fetchHomeSummary();
    } catch (error) {
      console.error('Error submitting picks:', error);
      setGlobalFeedback({ type: 'error', msg: 'Failed to submit. Please try again.' });
      setTimeout(() => setGlobalFeedback(null), 4000);
    } finally {
      setSubmittingAll(false);
    }
  };

  const formatDate = (dateString) => {
    const months = ["January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"];
    const [day, month, year] = dateString.split('/').map(Number);
    return `${months[month - 1]} ${day}, ${year}`;
  };

  const sortDates = (dates) => {
    return dates.sort((a, b) => {
      const [dayA, monthA, yearA] = a.split('/').map(Number);
      const [dayB, monthB, yearB] = b.split('/').map(Number);
      const dateA = new Date(Date.UTC(yearA, monthA - 1, dayA));
      const dateB = new Date(Date.UTC(yearB, monthB - 1, dayB));
      return dateA - dateB;
    });
  };

  const doSwitchSport = (sport) => {
    setLoading(true);
    setPicks({});
    sessionStorage.removeItem('fp_picks_draft');
    setSelectedSport(sport);
  };

  const setSetSelectedSport = (sport) => {
    if (sport === selectedSport) return;
    const hasPending = Object.values(picks).some(arr => arr.length > 0);
    if (hasPending) {
      setPendingSportSwitch(sport);
      return;
    }
    doSwitchSport(sport);
  };

  const totalPicks = Object.values(picks).reduce((sum, arr) => sum + arr.length, 0);

  // ── Derived dashboard stats ──────────────────────────────────────────────
  const authUser = authData.user || {};
  const rank       = authUser.rank  || null;
  const winStreak  = homeSummary?.user_stats?.current_win_streak ?? null;
  const loginStreak = homeSummary?.user_stats?.login_streak ?? null;
  const xp         = homeSummary?.user_stats?.xp ?? 0;
  const levelInfo  = getLevelInfo(xp);

  const recentForm   = homeSummary?.recent_form       ?? [];
  const rankCtx      = homeSummary?.rank_context      ?? null;
  const friendsPicks = homeSummary?.friends_picks      ?? [];
  const popularToday = homeSummary?.popular_today      ?? [];
  const nearAchs     = homeSummary?.near_achievements  ?? [];

  return (
    <BaseLayout isAuthenticated={isAuthenticated} user={user}>
      <RecentUpdatesModal show={showModal} handleClose={() => setShowModal(false)} />

      <Modal show={!!pendingSportSwitch} onHide={() => setPendingSportSwitch(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Unsaved Picks</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ margin: 0 }}>
            You have unsaved <strong>{selectedSport}</strong> picks. Switching to <strong>{pendingSportSwitch}</strong> will discard them.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPendingSportSwitch(null)}>Keep Picking</Button>
          <Button variant="danger" onClick={() => { doSwitchSport(pendingSportSwitch); setPendingSportSwitch(null); }}>Discard &amp; Switch</Button>
        </Modal.Footer>
      </Modal>

      <UsernameWelcomeModal
        show={showUsernameModal}
        handleClose={() => {
          localStorage.setItem(`fp_username_seen_${authData.user?.id}`, '1');
          setShowUsernameModal(false);
        }}
        currentUsername={authData.user?.username || ''}
        token={authData.token}
        onUsernameChanged={(newName) => {
          updateUserAttribute('username', newName);
          localStorage.setItem(`fp_username_seen_${authData.user?.id}`, '1');
          setShowUsernameModal(false);
        }}
      />

      <div className='homepage-container'>
        <div className="hp-layout">

          {/* ── Sidebar (desktop only) ────────────────────────────────────── */}
          <aside className="hp-sidebar">
            {summaryLoading ? (
              <div className="hp-sidebar-skeleton">
                <div className="hp-skel-stats" />
                <div className="hp-skel-widget" />
                <div className="hp-skel-widget" />
                <div className="hp-skel-widget" />
              </div>
            ) : (
              <>
                {/* Stats Card */}
                <div className="hp-stats-card">
                  <div className="hp-stats-grid">
                    <div className="hp-stat-tile">
                      <div className="hp-stat-label">Rank</div>
                      <div className="hp-stat-value">{rank ? `#${rank}` : '—'}</div>
                    </div>
                    <div className="hp-stat-tile">
                      <div className="hp-stat-label">Login Streak</div>
                      <div className="hp-stat-value" style={{ color: (loginStreak && loginStreak > 0) ? 'var(--flame)' : 'var(--text-dim)' }}>
                        {loginStreak !== null ? (loginStreak > 0 ? `🔥${loginStreak}` : '—') : '—'}
                      </div>
                    </div>
                    <div className="hp-stat-tile">
                      <div className="hp-stat-label">Win Streak</div>
                      <div className="hp-stat-value" style={{ color: (winStreak && winStreak > 0) ? 'var(--flame)' : 'var(--text-dim)' }}>
                        {winStreak !== null ? (winStreak > 0 ? `W${winStreak}` : '—') : '—'}
                      </div>
                    </div>
                  </div>
                  <div className="hp-xp-row">
                    <div className="hp-xp-header">
                      <span className="hp-level-badge">{levelInfo.levelName}</span>
                      <span className="hp-xp-amt">{xp.toLocaleString()} XP</span>
                    </div>
                    <div className="hp-xp-track">
                      <div className="hp-xp-fill" style={{ width: `${levelInfo.pct}%` }} />
                    </div>
                    <div className="hp-xp-subline">
                      {levelInfo.nextThr !== null
                        ? `${(levelInfo.nextThr - xp).toLocaleString()} XP to next level`
                        : 'Max level reached!'}
                    </div>
                  </div>
                </div>

                {/* Recent Form */}
                {recentForm.length > 0 && (
                  <div className="hp-widget">
                    <div className="hp-widget-heading">Recent Form</div>
                    <div className="hp-form-strip">
                      {[...recentForm].reverse().map((p, i) => (
                        <div
                          key={i}
                          className={`hp-form-pill hp-form-${p.result === 1 ? 'win' : 'loss'}`}
                          title={`${p.game} — ${p.result === 1 ? 'Win' : 'Loss'}`}
                          onClick={() => navigate(`/game/${p.game_id}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          <span className="hp-form-letter">{p.result === 1 ? 'W' : 'L'}</span>
                          <span className="hp-form-sport">{p.sport}</span>
                        </div>
                      ))}
                    </div>
                    <div className="hp-form-summary">
                      <span style={{ color: 'var(--green)' }}>{recentForm.filter(p => p.result === 1).length}W</span>
                      {' '}
                      <span style={{ color: 'var(--red)' }}>{recentForm.filter(p => p.result === 0).length}L</span>
                      {' '}
                      <span style={{ color: 'var(--text-dim)' }}>in last {recentForm.length}</span>
                    </div>
                  </div>
                )}

                {/* Rank & Progress */}
                <div className="hp-widget">
                  <div className="hp-widget-heading">Rank & Progress</div>
                  {rank ? (
                    <div className="hp-rank-block">
                      <div className="hp-rank-big">#{rank}</div>
                      {rankCtx ? (
                        <div className="hp-rank-chase" onClick={() => navigate(`/user/${rankCtx.above_username}`)} style={{ cursor: 'pointer' }}>
                          {rankCtx.score_diff > 0 ? (
                            <>
                              <span className="hp-rank-diff">+{rankCtx.score_diff}</span>
                              {' '}pts behind{' '}
                              <strong>@{rankCtx.above_username}</strong>
                            </>
                          ) : (
                            <>
                              <span className="hp-rank-diff">+{rankCtx.games_diff}</span>
                              {' '}{rankCtx.games_diff === 1 ? 'game' : 'games'} behind{' '}
                              <strong>@{rankCtx.above_username}</strong>
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="hp-rank-chase" style={{ color: 'var(--gold)' }}>
                          You're leading the pack! 🏆
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="hp-widget-empty" style={{ marginBottom: '0.6rem' }}>
                      Make picks to get ranked on the leaderboard.
                    </p>
                  )}
                  {nearAchs.length > 0 && (
                    <div className="hp-near-ach">
                      <div className="hp-near-heading">Almost There</div>
                      {nearAchs.slice(0, 1).map(ach => (
                        <div key={ach.key} className="hp-ach-card">
                          <div className="hp-ach-title">{ach.title}</div>
                          <div className="hp-ach-desc">{ach.description}</div>
                          <div className="hp-ach-progress-row">
                            <div className="hp-ach-track">
                              <div className="hp-ach-fill" style={{ width: `${Math.min(100, ach.pct)}%` }} />
                            </div>
                            <span className="hp-ach-frac">{ach.progress}/{ach.total}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Friends' Picks */}
                <div className="hp-widget">
                  <div className="hp-widget-heading">Friends' Picks</div>
                  {friendsPicks.length > 0 ? (
                    <div className="hp-friends-list">
                      {friendsPicks.map((fp, i) => {
                        const avatarSrc = getAvatarSrc(fp.profile_picture);
                        return (
                          <div
                            key={i}
                            className="hp-friend-row"
                            onClick={() => navigate(`/game/${fp.game_id}`)}
                          >
                            <div className="hp-friend-avatar-wrap">
                              <img
                                src={avatarSrc}
                                alt={fp.username}
                                className="hp-friend-avatar"
                                onError={e => { e.target.src = '/default.jpg'; }}
                              />
                            </div>
                            <div className="hp-friend-info">
                              <span className="hp-friend-name">@{fp.username}</span>
                              <span className="hp-friend-pick-detail">
                                {getTeamDisplayName(fp.picked_team)}&nbsp;
                                <span className="mp-sport-pill" style={{ fontSize: '0.58rem', padding: '1px 6px' }}>{fp.sport}</span>
                              </span>
                            </div>
                            <div className="hp-friend-meta">
                              {fp.result === 1 && <span className="hp-friend-result hp-result-w">W</span>}
                              {fp.result === 0 && <span className="hp-friend-result hp-result-l">L</span>}
                              <span className="hp-friend-ago">{fp.ago}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="hp-widget-empty">
                      No friends yet.{' '}
                      <button className="hp-link-btn" onClick={() => navigate('/friends')}>
                        Find players →
                      </button>
                    </p>
                  )}
                </div>
              </>
            )}
          </aside>

          {/* ── Main Content ──────────────────────────────────────────────── */}
          <div className="hp-main">

            {/* Compact stats strip — hidden on desktop, shown on mobile */}
            {!summaryLoading && (
              <div className="hp-mobile-stats">
                <div className="hp-ms-tile">
                  <span className="hp-ms-val">{rank ? `#${rank}` : '—'}</span>
                  <span className="hp-ms-label">Rank</span>
                </div>
                <div className="hp-ms-tile">
                  <span className="hp-ms-val" style={{ color: (loginStreak && loginStreak > 0) ? 'var(--flame)' : 'var(--text-dim)' }}>
                    {loginStreak !== null ? (loginStreak > 0 ? `🔥${loginStreak}` : '—') : '—'}
                  </span>
                  <span className="hp-ms-label">Login</span>
                </div>
                <div className="hp-ms-tile">
                  <span className="hp-ms-val" style={{ color: (winStreak && winStreak > 0) ? 'var(--flame)' : 'var(--text-dim)' }}>
                    {winStreak !== null ? (winStreak > 0 ? `W${winStreak}` : '—') : '—'}
                  </span>
                  <span className="hp-ms-label">Streak</span>
                </div>
                <div className="hp-ms-xp">
                  <div className="hp-ms-xp-top">
                    <span className="hp-level-badge">{levelInfo.levelName}</span>
                    <span className="hp-xp-amt">{xp.toLocaleString()} XP</span>
                  </div>
                  <div className="hp-xp-track" style={{ marginTop: '3px' }}>
                    <div className="hp-xp-fill" style={{ width: `${levelInfo.pct}%` }} />
                  </div>
                </div>
              </div>
            )}

            {/* Popular Today */}
            {!summaryLoading && popularToday.length > 0 && (
              <div className="hp-popular-bar">
                <span className="hp-popular-heading">Popular Today</span>
                <div
                  className="hp-popular-scroll"
                  ref={el => {
                    if (!el) return;
                    let isDown = false, startX = 0, scrollLeft = 0;
                    el.onmousedown = e => { isDown = true; el.classList.add('hp-pop-dragging'); startX = e.pageX - el.offsetLeft; scrollLeft = el.scrollLeft; };
                    el.onmouseleave = () => { isDown = false; el.classList.remove('hp-pop-dragging'); };
                    el.onmouseup = () => { isDown = false; el.classList.remove('hp-pop-dragging'); };
                    el.onmousemove = e => { if (!isDown) return; e.preventDefault(); el.scrollLeft = scrollLeft - (e.pageX - el.offsetLeft - startX); };
                  }}
                >
                  {popularToday.map((item, i) => (
                    <div key={i} className="hp-popular-chip">
                      <span className="mp-sport-pill">{item.sport}</span>
                      <span className="hp-popular-team">{getTeamDisplayName(item.team)}</span>
                      <span className="hp-popular-count">{item.count} picks</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pick Queue */}
            <div className="hp-pick-queue">
              <h1 className="homepage-title">This Week's Sports Picks</h1>
              <div className="sport-tabs">
                <button className={`sport-tab${selectedSport === 'NBA' ? ' active' : ''}`} onClick={() => setSetSelectedSport('NBA')}>NBA</button>
                <button className={`sport-tab${selectedSport === 'EPL' ? ' active' : ''}`} onClick={() => setSetSelectedSport('EPL')}>EPL</button>
                <button className={`sport-tab${selectedSport === 'MLS' ? ' active' : ''}`} onClick={() => setSetSelectedSport('MLS')}>MLS</button>
                <button className={`sport-tab${selectedSport === 'MLB' ? ' active' : ''}`} onClick={() => setSetSelectedSport('MLB')}>MLB</button>
              </div>
              {loading ? (
                <FlameLoader text="Loading games..." />
              ) : allPicksMade[selectedSport] ? (
                <div className="all-picks-banner">
                  <ExtinguishedFlame />
                  <p>You have made all upcoming picks for the {selectedSport}.</p>
                  <p>Visit the "History" page to view your picks.</p>
                </div>
              ) : (Object.keys(eventsByDate).length > 0) ? (
                sortDates(Object.keys(eventsByDate)).map((date, index) => {
                  const gamesForDate = eventsByDate[date].filter(game => gameVisibility[date]?.[game.game_id]);
                  return gamesForDate.length > 0 ? (
                    <div key={index} style={{ transition: 'opacity 0.3s, height 0.3s', opacity: 1, overflow: 'hidden' }}>
                      <h3 className="date-group-header">{formatDate(date)}</h3>
                      {gamesForDate.map((event, eventIndex) => (
                        <MatchPick
                          key={eventIndex}
                          homeTeam={event.home_team.toUpperCase()}
                          awayTeam={event.away_team.toUpperCase()}
                          gameid={event.game_id}
                          initialPick={picks[date]?.find(p => p.gameid === event.game_id)?.team ?? null}
                          onPickSelected={(team, gameid) => {
                            setPicks(currentPicks => {
                              const updatedPicks = currentPicks[date] ? currentPicks[date].filter(p => p.gameid !== gameid) : [];
                              if (team) { updatedPicks.push({ gameid, team }); }
                              return { ...currentPicks, [date]: updatedPicks };
                            });
                          }}
                          isEPL={(selectedSport === 'EPL' || selectedSport === 'MLS')}
                          isLoading={loading}
                        />
                      ))}
                    </div>
                  ) : null;
                })
              ) : (
                <div className="all-picks-banner">
                  <ExtinguishedFlame />
                  <p>No upcoming {selectedSport} games are currently available.</p>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      <div className={`picks-cart-bar${(totalPicks > 0 || globalFeedback) && !loading ? ' visible' : ''}`}>
        <div className="picks-cart-left">
          <i className="fa fa-fire picks-cart-icon" aria-hidden="true" />
          <span className="picks-cart-label">
            <span className="picks-cart-count">{totalPicks}</span> pick{totalPicks !== 1 ? 's' : ''} ready
          </span>
          {globalFeedback && (
            <span className={`picks-cart-feedback ${globalFeedback.type}`}>{globalFeedback.msg}</span>
          )}
        </div>
        <button
          className="picks-cart-submit"
          onClick={handleSubmitAllPicks}
          disabled={submittingAll || totalPicks === 0}
        >
          {submittingAll ? 'Submitting...' : 'Submit All Picks'}
        </button>
      </div>
    </BaseLayout>
  );
};

export default HomePage;
